// ./src/components/features/DashboardCards/DashboardCardsEditor.tsx
//
// ROADMAP #190 — the list the host arranges the event dashboard with: drag a card to move it, a switch to show or hide
// it. Presentational and controlled (`value` → `onChange`); the drawer owns the read and the save.
//
// ⓘ The drag is `@hello-pangea/dnd`, as `CategoryOrderEditor`'s: a row is its own handle, a dragged row is drawn in
//   `document.body` (the drawer's transform would offset it), and the keyboard moves a row too (Space, then the arrows).
//   The switch is a real control inside the row — the library does not start a drag from one.
// ⓘ Each card says WHEN it is drawn: a card that is on is still not drawn when it has nothing to show.
import React from "react";
import { createPortal } from "react-dom";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Box, Group, Stack, Switch, Text } from "@mantine/core";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic/AppIcon";
import { DASHBOARD_CARD, DashboardCardEventFacts, DashboardCardId, DashboardCardSetting } from "@/utils/dashboardCards";

export interface DashboardCardsEditorProps {
  /** The cards to arrange, in order (only the ones that apply here). */
  value: DashboardCardSetting[];
  onChange: (cards: DashboardCardSetting[]) => void;
  /** The event the dashboard is for; null for the account's default (a card is then named for every kind of event). */
  facts: DashboardCardEventFacts | null;
  disabled?: boolean;
}

interface CardCopy {
  label: string;
  /** When it is drawn — null for a card that always is. */
  when: string | null;
  icon: string;
}

/** The name, icon and "shown when" of each card. ⓘ The names are the cards' own titles. */
export const dashboardCardCopy = (id: DashboardCardId, facts: DashboardCardEventFacts | null, t: TFunction): CardCopy => {
  switch (id) {
    case DASHBOARD_CARD.ARRIVALS:
      return {
        label: t("events:dashCards.arrivals", { defaultValue: "Arrivals" }),
        when: t("events:dashCards.arrivalsWhen", { defaultValue: "On the day of the event, or once somebody has checked in." }),
        icon: "Login",
      };
    case DASHBOARD_CARD.REGISTRATIONS:
      return {
        label: !facts
          ? t("events:dashCards.registrations", { defaultValue: "Tickets sold or RSVPs" })
          : facts.ticketed
            ? t("events:dash.ticketsSold", { defaultValue: "Tickets sold" })
            : t("events:dash.rsvps", { defaultValue: "RSVPs" }),
        when: !facts ? t("events:dashCards.registrationsWhen", { defaultValue: "Tickets sold on a ticketed event, replies on any other." }) : null,
        icon: facts && !facts.ticketed ? "Users" : "Ticket",
      };
    case DASHBOARD_CARD.ADDONS:
      return {
        label: t("events:dash.addonsSold", { defaultValue: "Add-ons" }),
        when: t("events:dashCards.addonsWhen", { defaultValue: "When a ticketed event sells add-ons." }),
        icon: "ShoppingBag",
      };
    case DASHBOARD_CARD.VOUCHERS:
      return {
        label: t("events:dash.coupons", { defaultValue: "Vouchers" }),
        when: t("events:dashCards.vouchersWhen", { defaultValue: "Once the event has vouchers." }),
        icon: "Gift",
      };
    case DASHBOARD_CARD.VOLUNTEERS:
      return {
        label: t("events:dash.volunteers", { defaultValue: "Volunteers" }),
        when: t("events:dashCards.volunteersWhen", { defaultValue: "Once the event has volunteer shifts." }),
        icon: "HeartHandshake",
      };
    case DASHBOARD_CARD.ANSWERS:
      return {
        label: t("events:dash.answers", { defaultValue: "Question answers" }),
        when: t("events:dashCards.answersWhen", { defaultValue: "When the event asks its own questions." }),
        icon: "ClipboardList",
      };
    case DASHBOARD_CARD.WEATHER:
      return {
        label: t("events:dashCards.weather", { defaultValue: "Weather" }),
        when: t("events:dashCards.weatherWhen", {
          defaultValue:
            "When a forecast covers the event — not for an indoor event unless it is marked weather-sensitive. Hidden, the top card shows no forecast either.",
        }),
        icon: "Cloud",
      };
    case DASHBOARD_CARD.TASKS:
      return { label: t("events:dash.tasks", { defaultValue: "Tasks & appointments" }), when: null, icon: "ListCheck" };
    case DASHBOARD_CARD.BUDGET:
      return { label: t("events:dash.budget", { defaultValue: "Budget" }), when: null, icon: "Wallet" };
    case DASHBOARD_CARD.TIMELINE:
      return {
        label: t("events:dash.timeline", { defaultValue: "Timeline" }),
        when: t("events:dashCards.timelineWhen", { defaultValue: "Once the event has a timeline." }),
        icon: "Timeline",
      };
    case DASHBOARD_CARD.SIGNUPS:
      return {
        label: t("events:dash.signups", { defaultValue: "Sign-up list" }),
        when: t("events:dashCards.signupsWhen", { defaultValue: "Once the event has sign-up items." }),
        icon: "ClipboardList",
      };
    case DASHBOARD_CARD.POLLS:
      return {
        label: t("events:dash.polls", { defaultValue: "Polls & quizzes" }),
        when: t("events:dashCards.pollsWhen", { defaultValue: "Once the event has a poll or a quiz." }),
        icon: "MessageCircle",
      };
    default:
      return { label: id, when: null, icon: "LayoutDashboard" };
  }
};

export const DashboardCardsEditor: React.FC<DashboardCardsEditorProps> = ({ value, onChange, facts, disabled = false }) => {
  const { t } = useTranslation(["events", "common"]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const next = [...value];
    const [moved] = next.splice(result.source.index, 1);

    next.splice(result.destination.index, 0, moved);
    onChange(next);
  };

  const toggle = (id: DashboardCardId, shown: boolean) => {
    onChange(value.map((card) => (card.id === id ? { ...card, hidden: !shown } : card)));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="dashboardCards" isDropDisabled={disabled}>
        {(dropProvided) => (
          <Stack gap="xs" ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
            {value.map((card, index) => {
              const copy = dashboardCardCopy(card.id, facts, t);

              return (
                <Draggable key={card.id} draggableId={card.id} index={index} isDragDisabled={disabled}>
                  {(dragProvided, snapshot) => {
                    const row = (
                      <Group
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        // ⓘ The handle's own name, so a screen reader says which card it moves.
                        aria-label={t("events:dashCards.moveCard", { defaultValue: "Move {{card}}", card: copy.label })}
                        wrap="nowrap"
                        gap="sm"
                        p="sm"
                        align="flex-start"
                        style={{
                          border: "1px solid var(--mantine-color-default-border)",
                          borderRadius: 8,
                          background: snapshot.isDragging ? "var(--mantine-color-default-hover)" : "var(--mantine-color-body)",
                          ...dragProvided.draggableProps.style,
                        }}
                      >
                        {/* ⓘ Top-aligned with a nudge: a long "shown when" wraps on a phone. */}
                        <Box style={{ display: "flex", color: "var(--mantine-color-dimmed)", flexShrink: 0, marginTop: 4 }}>
                          <AppIcon iconName="GripVertical" size={18} />
                        </Box>
                        <Box style={{ display: "flex", flexShrink: 0, marginTop: 4, opacity: card.hidden ? 0.5 : 1 }}>
                          <AppIcon iconName={copy.icon} size={18} />
                        </Box>
                        <Stack gap={2} style={{ flex: 1, minWidth: 0, opacity: card.hidden ? 0.6 : 1 }}>
                          <Text size="md" fw={600}>
                            {copy.label}
                          </Text>
                          {copy.when && (
                            <Text size="md" c="dimmed">
                              {copy.when}
                            </Text>
                          )}
                        </Stack>
                        <Switch
                          size="md"
                          mt={2}
                          checked={!card.hidden}
                          disabled={disabled}
                          onChange={(e) => {
                            // ⓘ Read now: the event is gone by the time a state updater runs.
                            const shown = e.currentTarget.checked;

                            toggle(card.id, shown);
                          }}
                          aria-label={t("events:dashCards.showCard", { defaultValue: "Show {{card}}", card: copy.label })}
                          style={{ flexShrink: 0 }}
                        />
                      </Group>
                    );

                    return snapshot.isDragging ? createPortal(row, document.body) : row;
                  }}
                </Draggable>
              );
            })}
            {dropProvided.placeholder}
          </Stack>
        )}
      </Droppable>
    </DragDropContext>
  );
};
